/**
 * Billing Context
 *
 * Provides billing state and actions to the entire app.
 * Reads config.billing to determine entitlements and Stripe integration.
 */

'use client';

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import type { BillingConfig } from '../types/app-config';

// =============================================================================
// Types
// =============================================================================

export interface BillingContextValue {
  /** Whether billing is enabled */
  enabled: boolean;
  /** The billing provider */
  provider: BillingConfig['provider'];
  /** Check if the current plan includes a feature */
  checkEntitlement: (feature: string) => boolean;
  /** Open the Stripe pricing table (navigates to billing page) */
  openPricingTable: () => void;
  /** Open the Stripe customer portal */
  openCustomerPortal: () => void;
  /** Current user's plan tier (if set) */
  currentPlan: string | null;
  /** Set the current user's plan tier */
  setCurrentPlan: (plan: string | null) => void;
  /** Trigger metered usage flush/reporting for current period */
  flushMeteredUsage: () => Promise<void>;
}

const BillingContext = createContext<BillingContextValue | null>(null);

// =============================================================================
// Provider
// =============================================================================

interface BillingProviderProps {
  config?: BillingConfig;
  children: React.ReactNode;
}

export function BillingProvider({ config, children }: BillingProviderProps) {
  const [currentPlan, setCurrentPlan] = React.useState<string | null>(null);

  const enabled = !!config && config.provider !== 'none';
  const entitlements = config?.entitlements || {};

  const checkEntitlement = useCallback(
    (feature: string): boolean => {
      // If billing is not enabled, everything is allowed
      if (!enabled) return true;

      // If no entitlements defined, everything is allowed
      if (Object.keys(entitlements).length === 0) return true;

      // If no current plan, deny access to gated features
      const allowedPlans = entitlements[feature];
      if (!allowedPlans) return true; // Feature not gated

      if (!currentPlan) return false;
      return allowedPlans.includes(currentPlan) || allowedPlans.includes('*');
    },
    [enabled, entitlements, currentPlan],
  );

  const openPricingTable = useCallback(() => {
    // Navigate to billing page within the app
    window.location.href = '/billing';
  }, []);

  const openCustomerPortal = useCallback(() => {
    if (config?.customerPortalUrl) {
      window.open(config.customerPortalUrl, '_blank', 'noopener');
    }
  }, [config?.customerPortalUrl]);

  const flushMeteredUsage = useCallback(async () => {
    if (!config?.usageTrackerUrl) {
      return;
    }

    await fetch(`${config.usageTrackerUrl.replace(/\/+$/, '')}/billing/flush-monthly`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
  }, [config?.usageTrackerUrl]);

  const value = useMemo<BillingContextValue>(
    () => ({
      enabled,
      provider: config?.provider || 'none',
      checkEntitlement,
      openPricingTable,
      openCustomerPortal,
      currentPlan,
      setCurrentPlan,
      flushMeteredUsage,
    }),
    [enabled, config?.provider, checkEntitlement, openPricingTable, openCustomerPortal, currentPlan, flushMeteredUsage],
  );

  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  );
}

// =============================================================================
// Hooks
// =============================================================================

export function useBilling(): BillingContextValue {
  const context = useContext(BillingContext);
  if (!context) {
    // Return a no-op default so components outside BillingProvider still work
    return {
      enabled: false,
      provider: 'none',
      checkEntitlement: () => true,
      openPricingTable: () => {},
      openCustomerPortal: () => {},
      currentPlan: null,
      setCurrentPlan: () => {},
      flushMeteredUsage: async () => {},
    };
  }
  return context;
}
